package com.neurobs.ccabserver.translationadmin.states

import com.neurobs.ccab.db.{PhraseData, ProductVersionData}
import com.neurobs.ccab.translation.{CCABDiff, CCABDiffData, CCABVersionCreateData, PhraseDataView, UploadCCAB, UploadPhrase}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{GenericState, GenericStateGenerator, ServerDataAttribute, StateAttribute}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.{React, ReactElement, ReactRef}
import io.circe.generic.auto._
import org.scalajs.dom
import org.scalajs.dom.console

case class TestUploadState (
  productVersionState: ServerDataAttribute[Option[ProductVersionData]],
  uploadedFileState: StateAttribute[Option[String]],
  ccabDiffState: ServerDataAttribute[CCABDiffData],
  versionInputRef: ReactRef[dom.html.Input]
                           ) extends GenericState {

  def productVersion = productVersionState.value

  def parseVersionString(versionString: String): Option[BigDecimal] = {
    try {
      Some(BigDecimal(versionString.toDouble))
    } catch {
      case _: Throwable => None
    }

  }

  def loadLatestProductVersion(productId: Long): Unit = {
    TranslationServer.getProductVersions(productId)(handleServerProductVersions)
  }

  def handleServerProductVersions = ServerResponseUtil.handleObjectResultResponse[Seq[ProductVersionData]](
    handleObject = data => {
      val latestProductVersion = data.sortBy(_.version).last
      productVersionState.setData(Some(latestProductVersion))
    },
    onAnyError = productVersionState.setError(_)
  )

  def loadCCABDiff(productVersionId: Long, uploadCCAB: UploadCCAB): Unit = {
    TranslationServer.getCCABDiff(productVersionId, uploadCCAB)(handleCCABDiff)
  }

  def handleCCABDiff = ServerResponseUtil.handleObjectResultResponse[CCABDiffData](
    handleObject = ccabDiffState.setData(_),
    onAnyError = ccabDiffState.setError(_)
  )

  def createNewCCAB(newProductVersion: BigDecimal, productVersionData: ProductVersionData, ccabDiffData: CCABDiffData) = {
    uploadedFileState.setValue(None)
    val createData = CCABVersionCreateData(productVersionData.id, newProductVersion, productVersionData.productId, ccabDiffData)
    TranslationServer.createCCABVersion(createData)(handleNewServerProductVersion)
  }

  def handleNewServerProductVersion = ServerResponseUtil.handleObjectResultResponse[ProductVersionData](
    handleObject = data => productVersionState.setData(Some(data)),
    onAnyError = productVersionState.setError(_)
  )

}

object TestUploadStateGenerator extends GenericStateGenerator[TestUploadState] {

  override def generateEmptyDefaultState() = TestUploadState(
    emptyServerDataState[Option[ProductVersionData]](),
    emptyOptionDefaultState[String](),
    emptyServerDataState[CCABDiffData](),
    React.createRef[dom.html.Input]
  )

}

@react
object TestUploadStateProvider {
  case class Props(children: ReactElement*)
  val component = FunctionalComponent[Props] { props =>

    val productVersionState = ServerDataAttribute(useState[Either[Option[String], Option[ProductVersionData]]](Left(None)))
    val uploadedFileState = StateAttribute(useState[Option[String]](None))
    val ccabDiffState = ServerDataAttribute(useState[Either[Option[String], CCABDiffData]](Left(None)))
    val versionInputRef = React.createRef[dom.html.Input]

    val testUploadState = TestUploadState(
      productVersionState = productVersionState,
      uploadedFileState = uploadedFileState,
      ccabDiffState = ccabDiffState,
      versionInputRef = versionInputRef
    )

    useEffect(() => {
      testUploadState.loadLatestProductVersion(1)
    }, Vector())

    useEffect(() => {
      productVersionState.value.map(_.map(productVersionData =>
        uploadedFileState.value match {
          case None =>
          case Some(uploadString) =>
            io.circe.parser.decode[List[UploadPhrase]](uploadString) match {
              case Left(error) =>
                console.log(error)
                ccabDiffState.setError(error.toString)
              case Right(uploadPhrases) =>
                testUploadState.loadCCABDiff(productVersionData.id, UploadCCAB(uploadPhrases))
            }
        }
      ))
    }, Vector(uploadedFileState.value))

    TestUploadStateGenerator.Provider(testUploadState)(
      props.children:_*
    )

  }
}
